import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useCreatorsList } from '../../../hooks/useCreatorsList';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import { fmtNum } from '../../../lib/formatHelpers';

/**
 * Manager V2 Competition — Classement de l'équipe (leaderboard cleartext).
 * Route : /manager/competition + /manager/v2/competition.
 * Feature : pages.manager.competition.
 *
 * Trie par valid_days (proxy d'engagement, cleartext — pas de diamonds/$$ ici,
 * R2 : les montants restent côté detail créateur via decrypt).
 */
const SORT_KEYS = ['valid_days', 'tier_current'];

const ManagerV2Competition = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [sortKey, setSortKey] = useState('valid_days');
  const { rows, loading, error, reload } = useCreatorsList({
    managerId: userProfile?.id || null,
    includeDeparted: false,
  });

  const ranked = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return bv - av;
    });
  }, [rows, sortKey]);

  const medal = (i) =>
    i === 0 ? 'bg-warning/20 text-warning'
  : i === 1 ? 'bg-muted text-foreground'
  : i === 2 ? 'bg-accent/15 text-accent'
  : 'bg-muted/50 text-muted-foreground';

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                {t('competitionV2.title', 'Competition')}
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">{tenantName} · {rows.length} {t('competitionV2.creators', 'creators')}</p>
            </div>
            <div className="inline-flex rounded-lg overflow-hidden border border-border bg-card">
              {SORT_KEYS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSortKey(k)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    sortKey === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t(`competitionV2.sort.${k}`, k)}
                </button>
              ))}
            </div>
          </div>
        </header>
        <div className="p-4 md:p-6">
          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between mb-4">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={reload} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}
          {loading ? (
            <div className="rounded-2xl bg-card border border-border h-64 skeleton" />
          ) : ranked.length === 0 ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">
              {t('competitionV2.empty', 'No creators in your team yet.')}
            </div>
          ) : (
            <section className="rounded-2xl bg-card border border-border overflow-hidden">
              <ol className="divide-y divide-border">
                {ranked.map((c, i) => (
                  <li key={c.id} className="px-4 md:px-5 py-3 flex items-center gap-3">
                    <span className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${medal(i)}`}>
                      {i + 1}
                    </span>
                    <Link to={`/manager/team/${c.id}`} className="flex-1 min-w-0 hover:text-primary transition-colors">
                      <div className="font-medium text-foreground truncate">{c.display_name || c.tiktok_username || '—'}</div>
                      <div className="text-xs text-muted-foreground truncate">{c.server || '—'} · T{c.tier_current ?? c.tier ?? '—'}</div>
                    </Link>
                    <div className="text-right shrink-0">
                      <div className="font-heading font-bold tabular-nums">
                        {sortKey === 'valid_days' ? fmtNum(c.valid_days) : `T${c.tier_current ?? c.tier ?? '—'}`}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {sortKey === 'valid_days' ? t('competitionV2.validDays', 'valid days') : t('competitionV2.tier', 'tier')}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default ManagerV2Competition;
